import { Icon } from '@iconify/react';
import menu2Fill from '@iconify/icons-eva/menu-2-fill';
// material
import { alpha, styled } from '@mui/material/styles';
import {
  Box,
  Stack,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
} from '@mui/material';
// hooks
import { MHidden } from 'src/minimals/@material-extend';
import useCollapseDrawer from 'src/hooks/useCollapseDrawer';
// components
import { useSelector } from 'react-redux';
import { uiSelectors } from 'src/selectors';
import { generatePath, useParams, useLocation } from 'react-router';
import { PATH_ADMIN, PATH_PROFILE, PATH_WORKSPACE } from 'src/routes/paths';
import AccountPopover from './popovers/AccountPopover';
import LanguagePopover from './popovers/LanguagePopover';
import MonitoringNav from './MonitoringNav';
import SettingsSidebarButton from '../Sidebar/SettingsSidebarButton';
import { VoIPStatusIndicator } from 'src/common/VoIP';

// ----------------------------------------------------------------------

export const COLLAPSE_WIDTH = '102px';
export const DRAWER_WIDTH = '280px';
const APPBAR_MOBILE = 64;
const APPBAR_DESKTOP = 70;

const RootStyle = styled(AppBar, {
  shouldForwardProp: (prop) =>
    !['showDrawer', 'isCollapse', 'noSideBar'].includes(prop),
})(({ theme, showDrawer, isCollapse, noSideBar }) => ({
  boxShadow: 'none',
  // backdropFilter: 'blur(6px)',
  // WebkitBackdropFilter: 'blur(6px)', // Fix on Mobile
  backgroundColor: alpha(theme.palette.background.default, 0.72),

  [theme.breakpoints.up('lg')]: noSideBar
    ? {
        marginLeft: 0,
        width: '100%',
      }
    : {
        marginLeft: DRAWER_WIDTH,
        ...(isCollapse && {
          marginLeft: COLLAPSE_WIDTH,
        }),
        ...(!showDrawer && {
          marginLeft: 0,
        }),
        width: showDrawer
          ? `calc(100% - ${isCollapse ? COLLAPSE_WIDTH : DRAWER_WIDTH})`
          : '100%',
      },
}));

const ToolbarStyle = styled(Toolbar)(({ theme }) => ({
  minHeight: APPBAR_MOBILE,
  [theme.breakpoints.up('lg')]: {
    minHeight: APPBAR_DESKTOP,
  },
}));

const TitleContainer = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  paddingRight: theme.spacing(2),
  color: theme.palette.text.primary,
}));

// ----------------------------------------------------------------------

export default function DashboardNavbar({ onOpenSidebar, noSideBar, ...props }) {
  const { isCollapse } = useCollapseDrawer();
  const pageTitle = useSelector(uiSelectors.getPageTitle());
  const pageSubtitle = useSelector(uiSelectors.getPageSubtitle());
  const { pathname } = useLocation();
  const { workspaceId } = useParams();

  const showDrawer =
    pathname.includes(PATH_WORKSPACE.root.split(':')[0]) ||
    pathname === PATH_PROFILE.root ||
    pathname.startsWith(PATH_ADMIN.root);
  const isAccesingAsCustomer = useSelector(uiSelectors.isLoggedAsCustomer);

  return (
    <>
      <RootStyle
        showDrawer={showDrawer}
        isCollapse={isCollapse}
        noSideBar={noSideBar}
      >
        <ToolbarStyle>
          <MHidden width="lgUp">
            {!isAccesingAsCustomer && (
              <IconButton
                onClick={onOpenSidebar}
                sx={{ mr: 1, color: 'text.primary' }}
              >
                <Icon icon={menu2Fill} />
              </IconButton>
            )}
          </MHidden>

          <TitleContainer>
            <Typography variant="h4" noWrap>
              {pageTitle}
            </Typography>
            <Typography variant="body2">{pageSubtitle}</Typography>
          </TitleContainer>
          <Stack
            direction="row"
            alignItems="center"
            spacing={{ xs: 0.5, sm: 1.5 }}
          >
            {workspaceId &&
              pathname.includes(
                generatePath(PATH_WORKSPACE.settings.root, { workspaceId })
              ) && (
                <MHidden width="mdUp">
                  <SettingsSidebarButton />
                </MHidden>
              )}
            {workspaceId &&
              pathname.includes(
                generatePath(PATH_WORKSPACE.monitoring.root, { workspaceId })
              ) && (
                <MHidden width="mdDown">
                  <MonitoringNav />
                </MHidden>
              )}
            <VoIPStatusIndicator />
            <LanguagePopover />
            {/* <NotificationsPopover /> TODO: comentado por ahora */}
            <AccountPopover {...props} />
          </Stack>
        </ToolbarStyle>
      </RootStyle>
    </>
  );
}
